import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { FromStore } from '@cartwhl/common/types';
import Text from '@cartwhl/ui/base/Text';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import FileIcon from '@cartwhl/ui/icons/FileIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';
import UploadError from '@cartwhl/ui/molecules/UploadError';

export type FileUploadStatus =
  | 'UPLOADING'
  | 'QUEUED'
  | 'PROCESSING'
  | 'SUCCESS'
  | 'ERROR';

export interface FileUploadState {
  status: FileUploadStatus;
  fileTransferPercent?: number;
  processingPercentage?: number;
  errorReasons?: string[];
  createdAt?: string;
  uploadID?: string;
  estimatedProcessingTime?: number; // in ms
  warningMessage?: string;
}

export interface FileUploaderProps {
  fileContent?: string;
  accept: Record<string, string[]>;
  maxFileSize: number;
  upload?: FromStore<FileUploadState>;
  onDrop: (file: File) => void | Promise<void>;
  onRemove: () => void;
  onCancel?: (uploadID: string) => void;
  cancelable?: boolean;
  className?: string;
}

export function FileUploader({
  fileContent = 'File',
  accept,
  maxFileSize,
  upload,
  onDrop,
  onRemove,
  onCancel,
  cancelable,
  className,
}: FileUploaderProps) {
  const [fileError, setFileError] = useState<string | null>(null);

  const handleDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: any[]) => {
      if (rejectedFiles.length > 0) {
        setFileError(
          `Invalid file type. Please upload a ${getFileExtensions(
            accept,
          )} file.`,
        );
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (file.size > maxFileSize) {
          setFileError(
            `File size must be less than ${maxFileSize / (1024 * 1024)}MB`,
          );
          return;
        }
        setFileError(null);
        await onDrop(file);
      }
    },
    [maxFileSize, onDrop, accept],
  );

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop: handleDrop,
    accept,
    maxFiles: 1,
    multiple: false,
    disabled: upload && upload.status !== 'ERROR',
  });

  if (fileError) {
    return (
      <UploadError
        isLatestError
        onTryAgainClick={() => setFileError(null)}
        errorReasons={[fileError]}
        createdAt={new Date().toISOString()}
        primaryIcon={<WarningIcon />}
      />
    );
  }

  if (upload?.status === 'ERROR') {
    return (
      <UploadError
        isLatestError
        onTryAgainClick={onRemove}
        errorReasons={upload.errorReasons}
        createdAt={upload.createdAt || ''}
        primaryIcon={<WarningIcon />}
      />
    );
  }

  return (
    <div
      {...getRootProps()}
      className={`border-blue relative flex h-full min-h-24 w-full items-center justify-center overflow-hidden rounded-2xl border border-dashed p-4 transition-colors duration-200 ease-in-out dark:text-white
          ${
            upload
              ? 'bg-green/20 border-none'
              : isDragActive
                ? 'bg-blue/20'
                : 'bg-blue/10 hover:bg-blue/20'
          }
          ${upload ? 'cursor-default' : 'cursor-pointer'}
          ${className}
        `}
    >
      <input {...getInputProps()} disabled={Boolean(upload)} />
      <UploaderContent
        fileContent={fileContent}
        upload={upload}
        accept={accept}
        cancelable={cancelable}
        handleRemove={() => {
          if (upload?.status === 'PROCESSING' && upload?.uploadID) {
            onCancel?.(upload?.uploadID);
          }
          if (inputRef.current) {
            inputRef.current.value = '';
          }
          setFileError(null);
          onRemove();
        }}
      />
    </div>
  );
}

const getFileExtensions = (accept: Record<string, string[]>): string => {
  const extensions = Object.values(accept)
    .flat()
    .map((e) => e.replace('.', ''));
  if (!extensions.length) return '';

  // Remove dots and join with comma
  if (extensions.length === 1) {
    return extensions[0].replace('.', '');
  }
  return extensions.slice(0, -1).join(', ') + ' or ' + extensions.slice(-1);
};

function UploaderContent({
  fileContent,
  handleRemove,
  upload,
  accept,
  cancelable,
}: {
  fileContent: string;
  handleRemove: () => void;
  upload: FromStore<FileUploadState> | undefined;
  accept: Record<string, string[]>;
  cancelable?: boolean;
}) {
  if (!upload) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-1.5">
          <FileIcon className="text-blue" />
          <Text variant="studio/body/medium/sm">
            <span className="text-blue">Select File</span>{' '}
            <span className="hidden md:inline">or drag and drop one here</span>
          </Text>
        </div>
      </div>
    );
  }

  if (upload.status === 'UPLOADING') {
    return (
      <div className="flex h-full w-full items-center justify-between gap-1">
        <div className="flex flex-col items-start gap-1">
          <Text
            variant="studio/body/medium/sm"
            className="text-gray-60 dark:text-gray-45"
          >
            Step 1 of 2
          </Text>
          <Text variant="studio/body/medium/sm">
            {fileContent} uploading...{' '}
            <Percentage value={upload.fileTransferPercent} />
          </Text>
        </div>
        <div>
          {cancelable && handleRemove && (
            <Button
              variant="tertiary"
              size="sm"
              icon={<CrossIcon className="mx-0.5 h-4 w-4" />}
              onClick={handleRemove}
            />
          )}
        </div>
        <div
          className="bg-green ease absolute bottom-0 left-0 h-[3px] transition-[width]"
          style={{ width: `${upload.fileTransferPercent}%` }}
        />
      </div>
    );
  }
  if (upload.status === 'PROCESSING') {
    return (
      <div className="flex h-full w-full items-center justify-between gap-1">
        <div className="flex flex-col items-start gap-1">
          <Text
            variant="studio/body/medium/sm"
            className="text-gray-60 dark:text-gray-45"
          >
            Step 2 of 2
          </Text>
          <Text variant="studio/body/medium/sm">
            {fileContent} processing…{' '}
            <Percentage value={upload.processingPercentage} />
          </Text>
          {upload.estimatedProcessingTime && (
            <Text
              variant="studio/body/medium/sm"
              className="text-gray-60 dark:text-gray-45"
            >
              Approx.{' '}
              <span>
                {Math.round(upload.estimatedProcessingTime / 1000)} seconds
              </span>
            </Text>
          )}
          {upload.warningMessage && (
            <Text
              variant="studio/body/medium/sm"
              className="text-gray-60 dark:text-gray-45"
            >
              {upload.warningMessage}
            </Text>
          )}
        </div>
        <div>
          {cancelable && handleRemove && (
            <Button
              variant="tertiary"
              size="sm"
              icon={<CrossIcon className="mx-0.5 h-4 w-4" />}
              onClick={handleRemove}
            />
          )}
        </div>
        <div
          className="bg-green ease absolute bottom-0 left-0 h-[3px] transition-[width]"
          style={{ width: `${upload.processingPercentage}%` }}
        />
      </div>
    );
  }

  if (upload.status === 'SUCCESS') {
    return (
      <div className="flex h-full w-full items-center justify-between gap-1.5">
        <div className="flex items-center gap-2">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.88818 14.9131C8.63037 14.9131 8.41016 14.7985 8.22754 14.5693L5.44531 11.0889C5.37728 11.0065 5.32715 10.9259 5.29492 10.8472C5.26628 10.7684 5.25195 10.6878 5.25195 10.6055C5.25195 10.4193 5.31283 10.2653 5.43457 10.1436C5.5599 10.0218 5.71745 9.96094 5.90723 9.96094C6.12565 9.96094 6.31006 10.0594 6.46045 10.2563L8.8667 13.3447L13.5288 5.93799C13.6112 5.81266 13.6953 5.72493 13.7812 5.6748C13.8672 5.62109 13.9782 5.59424 14.1143 5.59424C14.3005 5.59424 14.4526 5.65332 14.5708 5.77148C14.689 5.88607 14.748 6.03646 14.748 6.22266C14.748 6.29785 14.7355 6.37484 14.7104 6.45361C14.6854 6.52881 14.646 6.60938 14.5923 6.69531L9.54346 14.564C9.3859 14.7967 9.16748 14.9131 8.88818 14.9131Z"
              fill="#50BF45"
            />
          </svg>
          <Text variant="studio/body/medium/sm">Complete!</Text>
        </div>
        <Button variant="primary" size="sm" onClick={handleRemove}>
          Done
        </Button>
      </div>
    );
  }
}

function Percentage({ value }: { value: number | undefined }) {
  if (!value) return <span className="inline-block w-12 text-left"></span>;
  return <span className="inline-block w-12 text-left">{value}%</span>;
}
